import { useEffect, useId, useImperativeHandle, useState, type KeyboardEvent, type Ref } from "react";
import { useCopy } from "./locale.tsx";

/** Something a configuration fits. Mirrors `FitTag` in the API contract, which re-validates on save. */
export interface FitTagValue {
  ref: string;
  name?: string;
}

/** A known ref offered while typing (e.g. the community fit directory). */
export interface FitTagSuggestion extends FitTagValue {
  /** How many published configurations carry it; shown as a hint. */
  count?: number;
  /** From an external catalog (e.g. Rebrickable): release year and a small picture. */
  year?: number;
  imageUrl?: string;
}

export const MAX_FIT_TAGS = 10;
const GENERIC_REF = /^[a-z0-9][a-z0-9.-]{0,31}$/;
const MAX_SUGGESTIONS = 6;
/** Remote lookups wait this long after the last keystroke. */
export const FIT_LOOKUP_DELAY_MS = 300;

export type FitTagError = "invalid" | "max" | "duplicate" | "empty";

/** Canonical ref: trimmed, lowercased, no whitespace, `suffix` appended when the ref has no "-" part (42115 -> 42115-1). */
export function normalizeFitInput(raw: string, suffix?: string): string {
  const ref = raw.trim().toLowerCase().replace(/\s+/g, "");
  return ref && suffix && !ref.includes("-") ? `${ref}${suffix}` : ref;
}

/** Normalise and validate a new tag against the existing ones and the brand's optional stricter pattern. */
export function addFitTag(existing: FitTagValue[], rawRef: string, rawName: string, pattern?: string, suffix?: string): { tags: FitTagValue[] } | { error: FitTagError } {
  const ref = normalizeFitInput(rawRef, suffix);
  if (!ref) return { error: "empty" };
  if (!GENERIC_REF.test(ref) || (pattern && !new RegExp(pattern, "i").test(ref))) return { error: "invalid" };
  if (existing.some((t) => t.ref === ref)) return { error: "duplicate" };
  if (existing.length >= MAX_FIT_TAGS) return { error: "max" };
  const name = rawName.trim().slice(0, 80);
  return { tags: [...existing, name ? { ref, name } : { ref }] };
}

/** Suggestions for what is typed: refs starting with it (or its digits) first, then names containing it; never ones already added. */
export function matchFitSuggestions(text: string, suggestions: FitTagSuggestion[], existing: FitTagValue[], limit = MAX_SUGGESTIONS): FitTagSuggestion[] {
  const q = text.trim().toLowerCase();
  if (!q) return [];
  const ref = q.replace(/\s+/g, "");
  const taken = new Set(existing.map((t) => t.ref));
  const free = suggestions.filter((s) => !taken.has(s.ref));
  const byRef = free.filter((s) => s.ref.startsWith(ref));
  const byName = free.filter((s) => !s.ref.startsWith(ref) && !!s.name?.toLowerCase().includes(q));
  return [...byRef, ...byName].slice(0, limit);
}

/**
 * Local matches (refs already in use) first, enriched with what the external
 * catalog knows about them, then the catalog's other matches; never refs
 * already added, at most `limit`.
 */
export function mergeFitSuggestions(local: FitTagSuggestion[], remote: FitTagSuggestion[], existing: FitTagValue[], limit = MAX_SUGGESTIONS + 2): FitTagSuggestion[] {
  const taken = new Set(existing.map((t) => t.ref));
  const byRef = new Map(remote.map((r) => [r.ref, r]));
  const merged: FitTagSuggestion[] = local.filter((l) => !taken.has(l.ref)).map((l) => ({ ...byRef.get(l.ref), ...l, name: l.name || byRef.get(l.ref)?.name }));
  const seen = new Set(merged.map((m) => m.ref));
  for (const r of remote) if (!seen.has(r.ref) && !taken.has(r.ref)) merged.push(r), seen.add(r.ref);
  return merged.slice(0, limit);
}

/** The set typed in a `FitPicker`, normalised and checked against the brand's pattern. */
export function parseFitInput(rawRef: string, rawName: string, pattern?: string, suffix?: string): FitTagValue | { error: FitTagError } {
  const r = addFitTag([], rawRef, rawName, pattern, suffix);
  return "error" in r ? r : r.tags[0];
}

/** Lets a form read the set in the field when it submits. */
export interface FitPickerHandle {
  /**
   * The set typed or picked, normalised; null when the field is empty or the
   * ref is invalid (the field then says why and the caller should stop).
   */
  take(): FitTagValue | null;
  /** Empty the field. */
  clear(): void;
  /** Put a set in the field, as if picked (e.g. the set the visitor searched for). */
  fill(value: FitTagSuggestion): void;
}

export interface FitPickerProps {
  /** Noun or question shown above the field, e.g. "Which LEGO® set does it fit?". */
  label: string;
  pattern?: string;
  /** Appended to refs typed without a "-" part (brand `fitTags.refSuffix`). */
  refSuffix?: string;
  refPlaceholder?: string;
  namePlaceholder?: string;
  /** Accessible name of the optional name input (defaults to the placeholder). */
  nameLabel?: string;
  /** Known refs to offer while typing; picking one fills in the ref and its name. */
  suggestions?: FitTagSuggestion[];
  /** Search an external catalog as the user types in either input (debounced, 2+ characters); failures just show no remote matches. */
  lookup?: (query: string) => Promise<FitTagSuggestion[]>;
  /** Help line under the inputs. */
  hint?: string;
  messages?: Partial<Record<FitTagError, string>>;
  /** Enter in either input. */
  onSubmit?: () => void;
  disabled?: boolean;
  ref?: Ref<FitPickerHandle>;
}

/**
 * One set: type its ref (and optionally its name), or type part of either and
 * pick a suggestion, which fills both in (with its picture when the catalog has
 * one). The form reads it with the handle's `take()`.
 */
export function FitPicker({ label, pattern, refSuffix, refPlaceholder, namePlaceholder, nameLabel, suggestions = [], lookup, hint, messages = {}, onSubmit, disabled, ref: handle }: FitPickerProps) {
  const id = useId();
  const copy = useCopy();
  const text: Partial<Record<FitTagError, string>> = { invalid: copy("fits.invalid"), ...messages };
  const [ref, setRef] = useState("");
  const [name, setName] = useState("");
  /** The suggestion the fields hold, until either is edited. */
  const [picked, setPicked] = useState<FitTagSuggestion | null>(null);
  const [error, setError] = useState<FitTagError | null>(null);
  const [remote, setRemote] = useState<{ query: string; results: FitTagSuggestion[] }>({ query: "", results: [] });
  const [searching, setSearching] = useState(false);
  // Suggestions follow the reference while one is typed, else the name ("Ford GT" finds 42154-1).
  const search = ref.trim() ? ref : name;
  const query = picked ? "" : search.trim();
  useEffect(() => {
    if (!lookup || query.length < 2) {
      setRemote({ query: "", results: [] });
      setSearching(false);
      return;
    }
    let live = true;
    setSearching(true);
    const timer = setTimeout(() => {
      lookup(query)
        .then((results) => live && setRemote({ query, results }))
        .catch(() => live && setRemote({ query, results: [] }))
        .finally(() => live && setSearching(false));
    }, FIT_LOOKUP_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [lookup, query]);
  const remoteResults = remote.query === query ? remote.results : [];
  const known = new Map([...remoteResults, ...suggestions].map((s) => [s.ref, s]));
  const matches = picked ? [] : mergeFitSuggestions(matchFitSuggestions(search, suggestions, []), remoteResults, []);

  const clear = () => {
    setRef("");
    setName("");
    setPicked(null);
    setError(null);
  };
  useImperativeHandle(handle, () => ({
    take: () => {
      const r = parseFitInput(ref, name || known.get(normalizeFitInput(ref, refSuffix))?.name || "", pattern, refSuffix);
      if ("error" in r) {
        setError(r.error);
        return null;
      }
      return r;
    },
    clear,
    fill: (s) => pick(s),
  }));
  function pick(s: FitTagSuggestion) {
    setRef(s.ref);
    setName(s.name ?? "");
    setPicked(s);
    setError(null);
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    // Enter with suggestions open takes the first one, else submits.
    if (matches.length && !picked) pick(matches[0]);
    else onSubmit?.();
  };
  const describedBy = [error ? `${id}-error` : "", hint ? `${id}-hint` : ""].filter(Boolean).join(" ") || undefined;

  return (
    // The error belongs to the reference input only (`.is-invalid` + aria-invalid there); the wrapper just gets `.has-error`.
    <div className={`cfg-field cfg-fits cfg-fit-picker${error ? " has-error" : ""}`}>
      <label className="cfg-label" htmlFor={id}>
        <span>{label}</span>
      </label>
      {picked?.imageUrl && (
        <p className="cfg-fit-picked">
          <img className="cfg-fit-suggestion-img" src={picked.imageUrl} alt="" width={48} height={36} />
          <strong>{picked.ref}</strong>
          {picked.name && <span>{picked.name}</span>}
          {picked.year && <small>{picked.year}</small>}
        </p>
      )}
      <div className="cfg-fit-row">
        <input
          id={id}
          className={`cfg-input cfg-fit-ref${error ? " is-invalid" : ""}`}
          placeholder={refPlaceholder}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required
          value={ref}
          disabled={disabled}
          onChange={(e) => {
            setRef(e.target.value);
            setPicked(null);
            if (error) setError(null);
          }}
          onKeyDown={onKey}
        />
        <input
          className="cfg-input cfg-fit-name"
          placeholder={namePlaceholder}
          aria-label={nameLabel ?? (namePlaceholder || copy("fits.nameLabel"))}
          autoComplete="off"
          value={name}
          maxLength={80}
          disabled={disabled}
          onChange={(e) => {
            setName(e.target.value);
            setPicked(null);
          }}
          onKeyDown={onKey}
        />
      </div>
      {matches.length > 0 && (
        <ul className="cfg-fit-suggestions" aria-label={copy("fits.suggestions")}>
          {matches.map((s) => (
            <li key={s.ref}>
              <button type="button" className="cfg-fit-suggestion" disabled={disabled} onClick={() => pick(s)}>
                {s.imageUrl && <img className="cfg-fit-suggestion-img" src={s.imageUrl} alt="" loading="lazy" width={32} height={24} />}
                <strong>{s.ref}</strong>
                {s.name && <span>{s.name}</span>}
                {s.year && <small>{s.year}</small>}
                {!!s.count && <small>×{s.count}</small>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {searching && matches.length === 0 && (
        <p className="cfg-fit-hint" aria-live="polite">
          {copy("fits.searching")}
        </p>
      )}
      {hint && (
        <p className="cfg-fit-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="cfg-error" id={`${id}-error`}>
          {text[error] ?? error}
        </p>
      )}
    </div>
  );
}
